/* fetch has to be ponyfilled directly because of mocked node contexts during hydrate */
import StoryblokClient, { ISbStoryData } from 'storyblok-js-client';

import { DomService } from '../dom';
import { calculateReadingTime } from '../reading-time';
import { StoryblokCategory } from './index.types';
import { StringService } from '../string';
import { RcmImageProp } from '../../components/rcm-image/rcm-image.types';

const { country, locale, storyblokAccessToken, storyblokAssetsAccessToken } = DomService.getGlobalConfig();
const countryCode = country && locale ? `${country}-${locale}` : 'at-de';

const storyblokClient = new StoryblokClient({
  accessToken: storyblokAccessToken,
});

const storyblokAssetsClient = new StoryblokClient({
  accessToken: storyblokAssetsAccessToken,
});

export const StoryblokService = {
  async fetchStories({
    inComponent,
    searchTerm,
    extraFilterQuery,
    // eslint-disable-next-line camelcase
    per_page = 100,
    sort_by = 'content.date:desc',
    page = 1,
  }): Promise<unknown> {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const params: any = { starts_with: countryCode, per_page, sort_by, page };
    if (inComponent) {
      params.filter_query = {
        component: {
          in: inComponent,
        },
      };
    }
    if (searchTerm) {
      params.search_term = searchTerm;
    }
    if (extraFilterQuery) {
      params.filter_query = {
        ...params.filter_query,
        ...extraFilterQuery,
      };
    }

    return storyblokClient.get('cdn/stories', params);
  },

  async getStoryblokStories(inComponent, searchTerm = ''): Promise<ISbStoryData[]> {
    let stories = [];
    const fetchedData = await this.fetchStories({ inComponent, searchTerm });
    if (fetchedData) {
      stories = await Promise.all(fetchedData.data.stories.map(async (story) => ({ ...story })));
    }
    return stories;
  },

  async getStory(uuid) {
    const data = await storyblokClient.get('cdn/stories', {
      by_uuids: uuid,
      resolve_links: 'story',
    });

    return data.data.stories[0];
  },

  async getArticles(perPage, page, userType) {
    let articles = [];
    const fetchedArticles = await this.fetchStories({
      inComponent: 'article',
      per_page: perPage,
      page,
      ...(userType && {
        extraFilterQuery: {
          user_type: {
            in_array: userType,
          },
        },
      }),
    });

    if (fetchedArticles) {
      articles = await Promise.all(
        fetchedArticles.data.stories.map(async (article) => ({
          ...article,
          readingTime: calculateReadingTime(article.content),
        })),
      );
    }

    return articles;
  },

  async getAllArticles(userType) {
    let allArticles = [];
    let page = 1;
    const perPage = 100;

    // eslint-disable-next-line no-constant-condition
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const articles = await this.getArticles(perPage, page, userType);

      if (!articles.length) break;

      allArticles = [...allArticles, ...articles];
      page += 1;
    }

    return allArticles;
  },

  async getRelatedArticlesByFundHashAndUserType(fundHash: string, userType: string) {
    let relatedArticles = [];

    const data = await this.fetchStories({
      inComponent: 'article',
      extraFilterQuery: {
        related_fund: {
          in: fundHash,
        },
        user_type: {
          in_array: userType,
        },
      },
      per_page: 4,
    });

    if (data) {
      relatedArticles = data.data.stories.map((article) => ({
        ...article,
        readingTime: calculateReadingTime(article.content),
      }));

      return relatedArticles;
    }

    return null;
  },

  async getRelatedArticles(category, uuid) {
    let relatedArticles = [];
    if (category) {
      const categoryArticles = await this.getArticlesByCategory(category.map((c) => c.uuid).join(','));

      if (categoryArticles) {
        relatedArticles = categoryArticles.filter((article) => article.uuid !== uuid);
      }
    }
    return relatedArticles;
  },

  async getArticlesByCategory(category) {
    let categoryArticles = [];

    const data = await this.fetchStories({
      extraFilterQuery: {
        category: {
          in_array: category,
        },
      },
      per_page: 100,
    });

    if (data) {
      categoryArticles = data.data.stories.map((article) => ({
        ...article,
        readingTime: calculateReadingTime(article.content),
      }));
    }

    return categoryArticles;
  },

  async getNewUserTypeSlug(newCountryCode = 'at-en') {
    const timeStamp = new Date().getTime();
    return storyblokClient.get('cdn/datasource_entries', {
      cv: timeStamp,
      per_page: 1000,
      dimension: newCountryCode,
      datasource: 'url',
    });
  },

  async getAllArticlesAndResolvedCategoriesByUserType(userType: string, page: number = 1, per_page: number = 25) {
    let articles = [];
    let categories = [];
    const articlesByUserType = await storyblokClient.get('cdn/stories', {
      resolve_relations: 'article.category',
      per_page,
      page,
      filter_query: {
        user_type: {
          any_in_array: userType,
        },
      },
      starts_with: countryCode,
      content_type: 'article',
    });

    articles = articlesByUserType.data?.stories;
    categories = articlesByUserType.data?.rels;

    if (articlesByUserType.total > articlesByUserType.perPage * page) {
      const nextPage = await this.getAllArticlesAndResolvedCategoriesByUserType(userType, page + 1, per_page);
      articles.push(...nextPage.data?.stories);
      nextPage.data?.rels.forEach((nextPageCategory) => {
        if (!categories.some((alreadyFetchedCategory) => alreadyFetchedCategory.uuid === nextPageCategory.uuid)) {
          categories.push(nextPageCategory);
        }
      });
    }
    return {
      data: {
        stories: articles,
        rels: categories,
      },
    };
  },

  async getCategories(userType: string): Promise<StoryblokCategory[]> {
    const articlesByUserType = await this.getAllArticlesAndResolvedCategoriesByUserType(userType);

    const resolvedCategories = articlesByUserType.data?.rels;

    const categories = new Map<string, any>();
    articlesByUserType.data?.stories.forEach((article) => {
      article?.content?.category.forEach((categoryOfArticleContent) => {
        const categoryUuid = categoryOfArticleContent.uuid;
        const resolvedCategoryOfArticle = resolvedCategories?.find((resolvedCategory) => resolvedCategory.uuid === categoryUuid);
        const alreadyMappedCategory = categories.get(categoryUuid);
        if (alreadyMappedCategory) {
          alreadyMappedCategory.totalArticles += 1;
        } else {
          categories.set(categoryUuid, {
            name: resolvedCategoryOfArticle?.name,
            uuid: resolvedCategoryOfArticle?.uuid,
            description: resolvedCategoryOfArticle?.content.description,
            image: resolvedCategoryOfArticle?.content.image_src,
            totalArticles: 1,
            // eslint-disable-next-line camelcase
            linkRetail: {
              src: resolvedCategoryOfArticle?.content.link_retail?.cached_url,
              target: resolvedCategoryOfArticle?.content.link_retail?.target,
            },
            // eslint-disable-next-line camelcase
            linkInstitutional: {
              src: resolvedCategoryOfArticle?.content.link_institutional?.cached_url,
              target: resolvedCategoryOfArticle?.content.link_institutional?.target,
            },
            showInArticleTopics: resolvedCategoryOfArticle?.content.show_in_article_topics,
          });
        }
      });
    });

    return Array.from(categories.values()) || [];
  },

  parseImageSrc(imageSrc: string | RcmImageProp, element: HTMLElement) {
    const initialWidth: string = Math.floor(element.getBoundingClientRect().width).toString();
    const initialHeight: string = Math.floor(element.getBoundingClientRect().height).toString();

    const parsed: string | RcmImageProp = StringService.stringToObject(imageSrc);
    let src: string = typeof parsed === 'string' ? parsed : (parsed || {}).src;
    if (src) {
      src = src.indexOf('focal(null)') > -1 ? src.replace('filters:focal(null)', '') : src;
      return `${(src || '').replace(/\$0x\$1/g, `${initialWidth}x${initialHeight}`)}`;
    }
    return '';
  },

  async getAssetInfoByUrl(assetUrl: string) {
    const data = await storyblokAssetsClient.get('cdn/assets/me', {
      filename: assetUrl,
    });
    return data.data.asset;
  },

  imageSrcToDownloadLink(imageSrc: string) {
    return imageSrc?.split('/m/')[0];
  },

  parseDatasourceToBoolean(datasourceValue: string | undefined): boolean {
    if (datasourceValue === '1') {
      return true;
    }
    return false;
  },

  parseDate(date: string): string {
    return new Date(date).toLocaleDateString('de-at', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  },
};
